import React from 'react';
import { Accordion, Box, Checkbox, Heading, Stack, TextLink } from '@ads-core/components';
import { CloseIcon } from '@ads-core/icons';
import { useRouter } from 'next/router';
import { useParams } from 'src/hooks/useParams';
import { baseQueryParams } from 'src/utils';
import { postalCodePattern } from '@alliander-fe/validation';
import { FilterMinMaxDate } from '../Filters/MinMaxDate';
import { FilterAddress } from '../Filters/Address';
import { isChecked } from './utils/isChecked';
import { setCheckboxFilter } from './utils/setCheckboxFilter';
import { useSetFilters } from './hooks/useFilters';

type filterOptions = {
  label: string;
  param: string;
  options: (string | undefined)[];
};

type FilterProps = {
  searchLabel?: string;
  dateFilterLabel?: string;
  options: filterOptions[];
  handleToggleFilters?: () => void;
};

export const Filters = ({
  options,
  handleToggleFilters,
  searchLabel,
  dateFilterLabel,
}: FilterProps) => {
  const filtersNames = options.map((option) => option.param);
  const [activeAccordionItems, setAccordionActiveItems] = React.useState<string[]>([
    'filter-op-adres',
    'filter-op-datum',
    ...filtersNames,
  ]);

  const { setFilter, clearFilters, activeFilters } = useSetFilters([
    ...filtersNames,
    baseQueryParams.postalCode,
    baseQueryParams.startDate,
    baseQueryParams.endDate,
  ]);

  const router = useRouter();
  const urlParams = useParams();

  const path = router.query.path || [];
  const location = path[path.length - 1];
  const locationIsPostalCode = path ? postalCodePattern.value.test(location) : false;

  const handleSetFilters = (filters: Partial<Record<keyof typeof baseQueryParams, string>>) => {
    setFilter(filters);

    if (handleToggleFilters) {
      handleToggleFilters();
    }
  };

  const handleClearFilters = () => {
    clearFilters();
    if (handleToggleFilters) {
      handleToggleFilters();
    }
  };

  return (
    <Stack gap={6}>
      {activeFilters ? (
        <Box paddingBottom={8}>
          <TextLink onClick={handleClearFilters} afterIcon={<CloseIcon />} asChild>
            <button>Wis alle filters ({activeFilters})</button>
          </TextLink>
        </Box>
      ) : null}

      <Accordion
        type="multiple"
        onAccordionOpen={setAccordionActiveItems}
        activeIds={activeAccordionItems}
      >
        <>
          <Accordion.Container firstChild>
            <Accordion.Item
              id="filter-op-adres"
              title={
                <Box paddingRight={10} asChild>
                  <Heading fontWeight="semiBold" size="paragraph" as="h3" isResponsive={false}>
                    {searchLabel}
                  </Heading>
                </Box>
              }
              content={
                <FilterAddress
                  onChangeValue={handleSetFilters}
                  value={{
                    ...urlParams,
                    postalCode: locationIsPostalCode ? location : urlParams.postalCode,
                  }}
                />
              }
              isActive={activeAccordionItems?.includes('filter-op-adres')}
              icon={
                <Accordion.Icon
                  variant="transparant"
                  tone="onLight"
                  size="small"
                  label="Zoek op locatie"
                  isActive={activeAccordionItems?.includes('filter-op-adres')}
                />
              }
            />
          </Accordion.Container>

          <Accordion.Container>
            <Accordion.Item
              id="filter-op-datum"
              title={
                <Box paddingRight={10} asChild>
                  <Heading fontWeight="semiBold" size="paragraph" as="h3" isResponsive={false}>
                    {dateFilterLabel}
                  </Heading>
                </Box>
              }
              content={
                <FilterMinMaxDate
                  minDateLabel="Aankondigingsdatum van"
                  maxDateLabel="Aankondigingsdatum tot"
                  onChangeValue={handleSetFilters}
                  value={urlParams}
                />
              }
              isActive={activeAccordionItems?.includes('filter-op-datum')}
              icon={
                <Accordion.Icon
                  variant="transparant"
                  tone="onLight"
                  size="small"
                  label="Zoek op locatie"
                  isActive={activeAccordionItems?.includes('filter-op-datum')}
                />
              }
            />
          </Accordion.Container>

          {options.map((filter, i) => {
            const isActive = activeAccordionItems?.includes(filter.param);

            return (
              <Accordion.Container key={`${i}-${filter.param}`}>
                <Accordion.Item
                  id={filter.param}
                  isActive={isActive}
                  title={
                    <Box paddingRight={10} asChild>
                      <Heading fontWeight="semiBold" size="paragraph" as="h3" isResponsive={false}>
                        {filter.label}
                      </Heading>
                    </Box>
                  }
                  content={
                    <Box paddingTop={4} asChild>
                      <Stack gap={4}>
                        {filter.options.map((item, index) =>
                          item ? (
                            <Checkbox
                              label={item}
                              key={`checkbox-${item}-${index}`}
                              checked={isChecked({
                                value: item,
                                param: filter.param,
                                activeFilters: urlParams,
                              })}
                              onClick={() => {
                                const setFilterParams = setCheckboxFilter(
                                  filter.param,
                                  item,
                                  urlParams
                                );

                                if (setFilterParams) {
                                  handleSetFilters(setFilterParams);
                                }
                              }}
                            />
                          ) : null
                        )}
                      </Stack>
                    </Box>
                  }
                  icon={
                    <Accordion.Icon
                      variant="transparant"
                      tone="onLight"
                      size="small"
                      label={filter.label}
                      isActive={isActive}
                    />
                  }
                />
              </Accordion.Container>
            );
          })}
        </>
      </Accordion>
    </Stack>
  );
};
